import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '../components/ApiErrorState.tsx';
import { Cargando } from '../components/FullScreenMessage.tsx';
import { TextField } from '../components/TextField.tsx';
import { useCompetitions, useCreateLeague } from '../features/leagues/hooks.ts';
import { temporadaPorDefecto } from '../features/leagues/temporada.ts';

export function CreateLeaguePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const competicionId = useId();
  const temporadaId = useId();

  const { data: competitions, isPending, error: errorCatalogo } = useCompetitions();
  const crear = useCreateLeague();

  const [name, setName] = useState('');
  const [competitionId, setCompetitionId] = useState<number | null>(null);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [errores, setErrores] = useState<{ name?: string }>({});

  // Preselección en cuanto llega el catálogo: la primera competición y su temporada en
  // curso. Un desplegable vacío obliga a dos toques que no aportan nada.
  useEffect(() => {
    if (!competitions || competitions.length === 0 || competitionId !== null) return;
    const primera = competitions[0]!;
    setCompetitionId(primera.id);
    setSeasonId(temporadaPorDefecto(primera));
  }, [competitions, competitionId]);

  const competicion = competitions?.find((c) => c.id === competitionId);

  function cambiarCompeticion(id: number) {
    setCompetitionId(id);
    // La temporada elegida pertenece a la competición anterior: el backend rechazaría
    // la combinación con `invalid_competition_season`, así que se reinicia aquí.
    const nueva = competitions?.find((c) => c.id === id);
    setSeasonId(nueva ? temporadaPorDefecto(nueva) : null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const limpio = name.trim();
    if (limpio.length < 2 || limpio.length > 60) {
      setErrores({ name: limpio === '' ? 'validation.leagueNameRequired' : 'validation.leagueNameLength' });
      return;
    }
    setErrores({});
    if (competitionId === null || seasonId === null) return;

    const { league } = await crear.mutateAsync({
      name: limpio,
      competition_id: competitionId,
      season_id: seasonId,
    });
    void navigate(`/leagues/${league.id}`, { replace: true });
  }

  if (isPending) return <Cargando text={t('common.loading')} />;
  if (errorCatalogo) return <ApiErrorState error={errorCatalogo} />;

  return (
    <>
      <h1 className="mb-4 text-xl font-bold">{t('leagues.create.title')}</h1>

      {competitions && competitions.length === 0 ? (
        <p className="aviso-atencion" data-test="sin-competiciones">
          {t('leagues.create.noCompetitions')}
        </p>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="tarjeta" data-test="formulario-crear-liga">
          <ApiErrorState error={crear.error} />

          <TextField
            label={t('leagues.create.name')}
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            required
            hint={t('leagues.create.nameHint')}
            error={errores.name ? t(errores.name) : undefined}
          />

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-semibold" htmlFor={competicionId}>
              {t('leagues.create.competition')}
            </label>
            <select
              id={competicionId}
              name="competition_id"
              className="campo"
              value={competitionId ?? ''}
              onChange={(e) => cambiarCompeticion(Number(e.target.value))}
            >
              {competitions?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-semibold" htmlFor={temporadaId}>
              {t('leagues.create.season')}
            </label>
            <select
              id={temporadaId}
              name="season_id"
              className="campo"
              value={seasonId ?? ''}
              onChange={(e) => setSeasonId(Number(e.target.value))}
            >
              {competicion?.seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <button className="btn-primario" type="submit" disabled={crear.isPending}>
            {crear.isPending ? t('leagues.create.pending') : t('leagues.create.submit')}
          </button>
          <p className="mt-3 text-center text-sm">
            <Link to="/" className="text-tenue">
              {t('leagues.create.cancel')}
            </Link>
          </p>
        </form>
      )}
    </>
  );
}
